import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { message, Modal } from "antd";

import useOptions from "features/masterData/hooks/useOptions";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import * as services from "../services/newsApi";
import { setIsLoading, setNewsList } from "../slices/newsSlice";

function toParams({ pagination = {}, sortBy = [] } = {}) {
  return {
    page: pagination.page || 0,
    size: pagination.pageSize || 10,
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
}

function useNewList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.news.isLoading);
  const newsList = useSelector((state) => state.news.newsList);
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);
  const { t, i18n } = useTranslation();
  const [dataSource, setDataSource] = useState([])
  const {

    // employeeTypeOptions,
    rolePermissionOptions,
    // getEmployeeTypeOptions,
    getRolePermission,
  } = useOptions();
  const [employeeOptions, setEmployeeOptions] = useState([]);
  useEffect(() => {
    if (newsList) {
      // console.log("setDataSource", newsList);
      // setDataSource
      setDataSource([
        ...newsList?.map?.(i => {
          return {
            ...i,
            audienceGroup: i?.audienceGroup?.map?.(o => i18n.language === 'en' ? o.nameEn : o.name)?.join(",")
          }
        }),
      ])
    }
  }, [newsList, i18n.language])

  const modifiedEmployeeOptions = useMemo(() => {
    const modifiedOptions = employeeOptions?.map?.((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.fullNameEn : opt.fullName,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n, employeeOptions]);

  const getEmployeeOptions = useCallback(async () => {
    if (typeof currentCompany !== "object") {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await services.getDropDown(values);
        setEmployeeOptions(response.data);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    }
  }, [currentCompany, dispatch]);


  useEffect(() => {
    getEmployeeOptions();
  }, [getEmployeeOptions]);


  const buildPagination = (page, size, totalItems = 0) => ({
    page: page + 1,
    size,
    total: totalItems,
    range: [page * size + 1, Math.min((page + 1) * size, totalItems)],
  });

  const queryRef = useRef({});

  const getNewsList = useCallback(
    async ({ page = 0, size = 10 } = {}) => {
      try {
        dispatch(setIsLoading(true));
        const { data } = await services.getAnnouncementNewsSettingPaging({
          ...queryRef.current,
          page,
          size,
          requestId: generateRandomString(),
          companyId: currentCompany,
        });
        dispatch(setNewsList(data?.content ?? []));
        setPagination(buildPagination(page, size, data?.totalItems));
      } catch {
        message.error(t("common.error"));
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, t]
  );

  const onSearch = useCallback(
    ({ page, size, ...filters } = {}) => {
      queryRef.current = { ...filters, sortBy: queryRef.current.sortBy };
      return getNewsList({ page: 0, size: 50 });
    },
    [getNewsList]
  );

  const handleOnChange = useCallback(
    (tablePagination, _, tableSorter) => {
      const { page, size, sortBy } = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: Array.isArray(tableSorter) ? tableSorter : [tableSorter],
      });
      queryRef.current = { ...queryRef.current, sortBy };
      getNewsList({ page, size });
    },
    [getNewsList]
  );

  const onCreate = useCallback(() => {
    navigate("/news_policy/news/create");
  }, [navigate]);

  const onSubmit = useCallback(() => {
    navigate("/news_policy/news");
  }, [navigate]);

  const onBack = useCallback(() => {
    navigate("/news_policy/news");
  }, [navigate]);

  const onCancel = useCallback((values) => {
    Modal.confirm({
      title: t("dialog.confirmation.header"),
      content: (
        <>
          <p>{t("are-you-sure-confirm-to-cancel")}</p>
        </>
      ),
      onOk() {
        message.success(t("common.success"))
      },
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
    });
  }, []);

  const onEdit = useCallback(
    (record) => {
      navigate(`/news_policy/news/${record.announcementNewsId}/edit`);
    },
    [navigate]
  );
  const onDelete = useCallback(
    (values) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>{t("dialog.confirmation.delete")}</p>
          </>
        ),
        onOk() {
          message.success(t("common.success"))
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [navigate]
  );

  // const handleOnChange = useCallback(
  //   (tablePagination, _, tableSorter) => {
  //     setPagination(tablePagination);
  //     const modifiedSorter = Array.isArray(tableSorter)
  //       ? [...tableSorter]
  //       : [tableSorter];
  //     setSorter(modifiedSorter);
  //     const modifiedParams = toParams({
  //       pagination: {
  //         page: tablePagination.current - 1,
  //         pageSize: tablePagination.pageSize,
  //       },
  //       sortBy: modifiedSorter,
  //     });
  //     // TODO: If sorting or filtering has changed, Change page to 0
  //     getNewsList(modifiedParams);
  //   },
  //   [getNewsList]
  // );

  const handleStatusChange = useCallback(
    (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: t("common.do-you-want-to-change"),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        async onOk() {
          try {
            dispatch(setIsLoading(true));
            await services.manageAnnouncementNewsStatus({
              announcementNewsId: record.announcementNewsId,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: val ? "ACTIVE" : "INACTIVE",
            });
            message.success(t("common.success"));
            await getNewsList({ page: pagination.page - 1, size: pagination.size });
          } catch {
            message.error(t("common.error"));
          } finally {
            dispatch(setIsLoading(false));
          }
        },
      });
    },
    [currentCompany, pagination.page, pagination.size, dispatch, getNewsList, t]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      queryRef.current = {};
      getNewsList({ page: 0, size: 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  useEffect(() => {
    getRolePermission();
  }, [getRolePermission]);

  return {
    isLoading,
    pagination,
    newsList: dataSource,
    rolePermissionOptions,
    announcementByOptions: modifiedEmployeeOptions,
    modifiedEmployeeOptions,
    onDelete,
    onCreate,
    onSearch,
    onEdit,
    onSubmit,
    onBack,
    onCancel,
    onChange: handleOnChange,
    onStatus: handleStatusChange
  };
}

export default useNewList;
